import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { carrouselResources } from '../constants';
import style from "../style";

const Carousel = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const scrollContainerRef = useRef(null);

  const categories = {
    diseño: { name: 'Diseño', color: 'border-blue-500 text-blue-600 bg-blue-50' },
    fabricacion: { name: 'Fabricación', color: 'border-green-500 text-green-600 bg-green-50' },
    electronica: { name: 'Electrónica', color: 'border-purple-500 text-purple-600 bg-purple-50' },
    testing: { name: 'Pruebas', color: 'border-orange-500 text-orange-600 bg-orange-50' },
    final: { name: 'Resultado', color: 'border-secondary text-secondary bg-secondary/10' }
  };

  const getCategory = (title) => {
    if (!title) return categories.diseño;
    const titleLower = title.toLowerCase();
    if (titleLower.includes('diseño') || titleLower.includes('conceptual')) return categories.diseño;
    if (titleLower.includes('fabricación') || titleLower.includes('montaje') || titleLower.includes('ruedas')) return categories.fabricacion;
    if (titleLower.includes('electrónic') || titleLower.includes('cableado')) return categories.electronica;
    if (titleLower.includes('probando') || titleLower.includes('testeando')) return categories.testing;
    if (titleLower.includes('final') || titleLower.includes('resultado')) return categories.final;
    return categories.diseño;
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="actividades" className="py-24 relative overflow-hidden scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="h-px w-8 bg-secondary/50"></span>
            <span className="text-secondary font-poppins font-semibold uppercase tracking-[0.3em] text-sm">Galería del Proyecto</span>
            <span className="h-px w-8 bg-secondary/50"></span>
          </div>
          
          <h2 className={`${style.heading2} mb-6 uppercase`}>
            Nuestras Actividades
          </h2>
          <p className={`${style.paragraph} max-w-2xl mx-auto text-slate-500`}>
            Explora el proceso de desarrollo e innovación detrás del proyecto VAE.
          </p>
        </motion.div>

        <div className="flex justify-between items-end mb-10">
            <div className="bg-slate-100 px-4 py-2 rounded-full border border-slate-200">
                <span className="text-sm font-semibold text-slate-500">
                  {carrouselResources.length} Entregas documentadas
                </span>
            </div>
          
          <div className="flex gap-4">
            <button 
              onClick={() => scroll('left')}
              className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 hover:border-secondary transition-all group shadow-sm"
            >
              <svg className="w-6 h-6 text-darkBlue group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              onClick={() => scroll('right')}
              className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 hover:border-secondary transition-all group shadow-sm"
            >
              <svg className="w-6 h-6 text-darkBlue group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div className="relative">
          <div 
            ref={scrollContainerRef}
            className="flex gap-8 overflow-x-auto no-scrollbar pb-12 scroll-smooth"
          >
            {carrouselResources.map((resource, index) => {
              const category = getCategory(resource.title);
              return (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex-shrink-0 w-[350px] group"
                >
                  <div 
                    className="bg-white rounded-3xl overflow-hidden cursor-pointer border border-slate-100 hover:border-secondary/40 transition-all duration-500 shadow-lg hover:shadow-2xl relative"
                    onClick={() => setSelectedImage(resource)}
                  >
                    <div className="relative h-60 overflow-hidden">
                      <img 
                        src={resource.image} 
                        alt={resource.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-darkBlue/20 to-transparent group-hover:from-darkBlue/10 duration-500" />
                      
                      <div className={`absolute top-4 left-4 border ${category.color} px-3 py-1 rounded-full backdrop-blur-md shadow-sm`}>
                        <span className="text-[10px] font-bold uppercase tracking-widest leading-none">
                          {category.name}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-8 bg-white">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-3xl font-bold text-slate-100 font-poppins">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="h-px flex-1 bg-slate-100 mx-4"></div>
                        <span className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-semibold">Fase {index + 1}</span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-darkBlue mb-2 leading-tight group-hover:text-secondary transition-colors line-clamp-2 min-h-[3.5rem]">
                        {resource.title || `Actividad ${index + 1}`}
                      </h3>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <AnimatePresence>
          {selectedImage && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/60 z-[200] flex items-center justify-center p-6 backdrop-blur-xl"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-[2.5rem] max-w-6xl w-full overflow-hidden relative border border-slate-100 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col lg:flex-row h-full">
                    <div className="lg:w-2/3 bg-slate-50 flex items-center justify-center border-b lg:border-b-0 lg:border-r border-slate-100">
                        <img 
                          src={selectedImage.image} 
                          alt={selectedImage.title}
                          className="w-full h-auto max-h-[70vh] object-contain"
                        />
                    </div>
                    
                    <div className="lg:w-1/3 p-10 flex flex-col justify-between">
                        <div>
                            <div className="flex justify-between items-center mb-8">
                                <span className={`border ${getCategory(selectedImage.title).color} px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-sm`}>
                                  {getCategory(selectedImage.title).name}
                                </span>
                                <button 
                                  onClick={() => setSelectedImage(null)}
                                  className="w-10 h-10 bg-slate-100 hover:bg-slate-200 text-darkBlue rounded-full flex items-center justify-center transition-all"
                                >
                                  ×
                                </button>
                            </div>
                            
                            <h3 className="text-3xl font-bold text-darkBlue mb-6 leading-tight font-poppins uppercase tracking-tight">
                              {selectedImage.title || "Documentación del Proyecto"}
                            </h3>
                            
                            <div className="w-12 h-1 bg-secondary rounded-full mb-8"></div>
                                                      <p className="text-slate-500 leading-relaxed mb-4 font-normal">
                                Esta es una pieza clave en el desarrollo tecnológico del VAE, demostrando el compromiso con la excelencia técnica y la innovación constante.
                            </p>
                        </div>
                        
                        <div className="text-[10px] text-slate-400 uppercase tracking-[0.3em] font-bold border-t border-slate-100 pt-6">
                            Archivo VAE // 2026
                        </div>
                    </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Carousel;